<script>
  import { onMount } from 'svelte';
  import { authFetch } from '../lib/api.js';
  import { showAlert } from '../lib/stores.js';
  
  let loading = true;
  let error = null;
  let categories = [];
  let selectedCategoryId = '';
  let selectedTitleId = '';
  let titles = [];
  let reportType = 'all';
  let dateRange = {
    start: '',
    end: ''
  };
  let generatingPdf = false;
  let exportingData = false;
  
  // Load titles
  async function fetchTitles() {
    try {
      const response = await authFetch('/api/vouchers/titles');
      
      if (response.success) {
        titles = response.titles;
      } else {
        throw new Error(response.message || 'Failed to load titles');
      }
    } catch (err) {
      console.error('Error loading titles:', err);
      error = 'Failed to load voucher titles. Please try again.';
      showAlert('error', error);
    }
  }
  
  // Load categories based on selected title
  async function fetchCategories() {
    try {
      if (!selectedTitleId) {
        categories = [];
        return;
      }
      
      const response = await authFetch(`/api/vouchers/categories/${selectedTitleId}`);
      
      if (response.success) {
        categories = response.categories;
      } else {
        throw new Error(response.message || 'Failed to load categories');
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      error = 'Failed to load voucher categories. Please try again.';
      showAlert('error', error);
    } finally {
      loading = false;
    }
  }
  
  // Generate voucher report PDF
  async function generateReport() {
    if (reportType === 'date' && (!dateRange.start || !dateRange.end)) {
      showAlert('error', 'Pilih rentang tanggal untuk laporan');
      return;
    }
    
    try {
      generatingPdf = true;
      
      const params = new URLSearchParams();
      
      if (selectedTitleId) {
        params.append('titleId', selectedTitleId);
      }
      
      if (selectedCategoryId) {
        params.append('categoryId', selectedCategoryId);
      }
      
      if (reportType === 'date') {
        params.append('startDate', dateRange.start);
        params.append('endDate', dateRange.end);
      } else if (reportType === 'used') {
        params.append('status', 'used');
      } else if (reportType === 'unused') {
        params.append('status', 'unused');
      }
      
      // Use fetch with blob response
      const response = await fetch(`/api/export/vouchers?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate report');
      }
      
      // Get the PDF blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link and click it to download the file
      const a = document.createElement('a');
      a.href = url;
      a.download = 'voucher-report.pdf';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showAlert('success', 'Laporan berhasil diunduh');
    } catch (err) {
      console.error('Error generating report:', err);
      showAlert('error', err.message || 'Gagal membuat laporan');
    } finally {
      generatingPdf = false;
    }
  }
  
  // Handle title selection change
  function handleTitleChange() {
    selectedCategoryId = '';
    fetchCategories();
  }
  
  // Export functions
  async function exportVouchersToExcel() {
    try {
      exportingData = true;
      
      // Get filters
      const params = new URLSearchParams();
      
      if (selectedTitleId) {
        params.append('titleId', selectedTitleId);
      }
      
      if (selectedCategoryId) {
        params.append('categoryId', selectedCategoryId);
      }
      
      // Use fetch
      const response = await fetch(`/api/export/vouchers/excel?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to export data');
      }
      
      // Get blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vouchers-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showAlert('success', 'Data voucher berhasil diexport');
    } catch (error) {
      console.error('Error exporting vouchers:', error);
      showAlert('error', error.message || 'Gagal export data voucher');
    } finally {
      exportingData = false;
    }
  }
  
  async function exportCategoriesToCSV() {
    try {
      exportingData = true;
      
      // Get filters
      const params = new URLSearchParams();
      
      if (selectedTitleId) {
        params.append('titleId', selectedTitleId);
      }
      
      // Use fetch
      const response = await fetch(`/api/export/categories/csv?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to export data');
      }
      
      // Get blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `categories-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showAlert('success', 'Data kategori berhasil diexport');
    } catch (error) {
      console.error('Error exporting categories:', error);
      showAlert('error', error.message || 'Gagal export data kategori');
    } finally {
      exportingData = false;
    }
  }
  
  async function exportStatisticsToExcel() {
    try {
      exportingData = true;
      
      // Use fetch
      const response = await fetch('/api/export/statistics/excel', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to export data');
      }
      
      // Get blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statistics-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showAlert('success', 'Data statistik penggunaan berhasil diexport');
    } catch (error) {
      console.error('Error exporting statistics:', error);
      showAlert('error', error.message || 'Gagal export data statistik');
    } finally {
      exportingData = false;
    }
  }
  
  // Initialize component
  onMount(async () => {
    await fetchTitles();
    loading = false;
  });
</script>

<div class="reports-container">
  <div class="page-header">
    <h1>Laporan</h1>
  </div>
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading data...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p>{error}</p>
      <button on:click={fetchTitles}>Coba Lagi</button>
    </div>
  {:else}
    <div class="report-card">
      <div class="card-header">
        <h2>Cetak Laporan Voucher</h2>
      </div>
      
      <div class="card-content">
        <div class="form-group">
          <label for="title-select">Judul Voucher</label>
          <select id="title-select" bind:value={selectedTitleId} on:change={handleTitleChange}>
            <option value="">Semua Judul</option>
            {#each titles as title}
              <option value={title._id}>{title.name}</option>
            {/each}
          </select>
        </div>
        
        <div class="form-group">
          <label for="category-select">Kategori Voucher</label>
          <select id="category-select" bind:value={selectedCategoryId} disabled={!selectedTitleId}>
            <option value="">Semua Kategori</option>
            {#each categories as category}
              <option value={category._id}>
                {category.titleId.name} - Rp {category.nominal.toLocaleString('id-ID')}
              </option>
            {/each}
          </select>
        </div>
        
        <div class="form-group">
          <label for="report-type">Tipe Laporan</label>
          <select id="report-type" bind:value={reportType}>
            <option value="all">Semua Voucher</option>
            <option value="used">Voucher Terpakai</option>
            <option value="unused">Voucher Belum Terpakai</option>
            <option value="date">Berdasarkan Tanggal Penggunaan</option>
          </select>
        </div>
        
        {#if reportType === 'date'}
          <div class="form-row">
            <div class="form-group">
              <label for="start-date">Tanggal Mulai</label>
              <input 
                type="date" 
                id="start-date" 
                bind:value={dateRange.start}
              />
            </div>
            
            <div class="form-group">
              <label for="end-date">Tanggal Akhir</label>
              <input 
                type="date" 
                id="end-date" 
                bind:value={dateRange.end}
                min={dateRange.start}
              />
            </div>
          </div>
        {/if}
        
        <div class="form-actions">
          <button 
            class="generate-button" 
            on:click={generateReport}
            disabled={generatingPdf}
          >
            {#if generatingPdf}
              <span class="button-spinner"></span>
              Generating...
            {:else}
              Generate PDF
            {/if}
          </button>
        </div>
      </div>
    </div>
    
    <div class="report-card">
      <div class="card-header">
        <h2>Export Data</h2>
      </div>
      
      <div class="card-content">
        <div class="export-option">
          <div class="export-info">
            <h3>Export Semua Data Voucher</h3>
            <p>Download data lengkap semua voucher dalam format Excel (.xlsx)</p>
          </div>
          <button class="export-button" on:click={exportVouchersToExcel} disabled={exportingData}>
            {#if exportingData}
              <span class="button-spinner"></span>
              Exporting...
            {:else}
              Export Excel
            {/if}
          </button>
        </div>
        
        <div class="export-option">
          <div class="export-info">
            <h3>Export Data Kategori</h3>
            <p>Download data kategori voucher dalam format CSV</p>
          </div>
          <button class="export-button" on:click={exportCategoriesToCSV} disabled={exportingData}>
            {#if exportingData}
              <span class="button-spinner"></span>
              Exporting...
            {:else}
              Export CSV
            {/if}
          </button>
        </div>
        
        <div class="export-option">
          <div class="export-info">
            <h3>Export Statistik Penggunaan</h3>
            <p>Download statistik penggunaan voucher dalam format Excel (.xlsx)</p>
          </div>
          <button class="export-button" on:click={exportStatisticsToExcel} disabled={exportingData}>
            {#if exportingData}
              <span class="button-spinner"></span>
              Exporting...
            {:else}
              Export Excel
            {/if}
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .reports-container {
    padding: 0;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .page-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0;
    letter-spacing: -0.025em;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 0;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .loading-spinner, .button-spinner {
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  .loading-spinner {
    width: 3rem;
    height: 3rem;
    margin-bottom: 1.25rem;
  }
  
  .button-spinner {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    display: inline-block;
    vertical-align: middle;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem;
    background-color: #fee2e2;
    border-radius: 0.5rem;
    color: #b91c1c;
    text-align: center;
  }
  
  .error-container button {
    margin-top: 1rem;
    padding: 0.5rem 1.25rem;
    background-color: #dc2626;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .error-container button:hover {
    background-color: #b91c1c;
  }
  
  .report-card {
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 1.5rem;
    overflow: hidden;
  }
  
  .card-header {
    background-color: #f8fafc;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .card-header h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e40af;
  }
  
  .card-content {
    padding: 1.5rem;
  }
  
  .form-group {
    margin-bottom: 1.25rem;
  }
  
  .form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 0.5rem;
  }
  
  .form-group select, .form-group input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background-color: white;
    font-size: 0.875rem;
    color: #1f2937;
  }
  
  .form-group select:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
  
  .form-row {
    display: flex;
    gap: 1rem;
  }
  
  .form-row .form-group {
    flex: 1;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }
  
  .generate-button {
    padding: 0.625rem 1.25rem;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
  }
  
  .generate-button:hover:not(:disabled) {
    background-color: #2563eb;
  }
  
  .generate-button:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
  }
  
  .export-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .export-option:last-child {
    border-bottom: none;
  }
  
  .export-info h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.25rem 0;
  }
  
  .export-info p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }
  
  .export-button {
    padding: 0.5rem 1rem;
    background-color: #10b981;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.75rem;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .export-button:hover {
    background-color: #059669;
  }
  
  @media (max-width: 768px) {
    .form-row {
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .export-option {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }
    
    .export-button {
      align-self: flex-start;
    }
  }
</style>